import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Spinner, Alert } from 'react-bootstrap';
import api from '../services/api';
import { showToast } from './ToastNotification';

const formatPrice = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  return num.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (value) => {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
};

/**
 * Potvrzení platby k zálohové faktuře (daňový doklad k přijaté platbě, řada 2613).
 *
 * Při plné úhradě se potvrzuje celá záloha, při částečné se vyberou položky,
 * které přijatá platba pokrývá — tak to dělá účetní i dnes. DZP dokladu je
 * datum přijetí platby a platba se na doklad zkopíruje, takže je rovnou uhrazený.
 */
function InvoiceAdvanceModal({ show, invoiceId, onHide, openTab }) {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !invoiceId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.getInvoiceAdvanceInfo(invoiceId);
        if (cancelled) return;
        const data = res.data || null;
        setInfo(data);
        // Výchozí výběr jsou hlavní řádky (slevy se k nim doberou samy na backendu).
        setSelected(new Set((data?.items || []).filter((i) => !String(i.number || '').includes('-')).map((i) => i.id)));
      } catch (e) {
        if (!cancelled) setError(e.message || 'Nepodařilo se načíst zálohu.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [show, invoiceId]);

  const mainItems = (info?.items || []).filter((i) => !String(i.number || '').includes('-'));
  const discountsFor = (number) => (info?.items || []).filter((i) => String(i.number || '').startsWith(`${number}-`));

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Součet vybraných položek včetně jejich slev a DPH — porovnává se s přijatou platbou.
  const selectedTotal = mainItems
    .filter((i) => selected.has(i.id))
    .reduce((sum, item) => {
      const rows = [item, ...discountsFor(item.number)];
      return sum + rows.reduce((acc, r) => {
        const line = (parseFloat(r.price) || 0) * (parseFloat(r.quantity) || 0);
        return acc + line + line * ((parseFloat(r.vat_rate) || 0) / 100);
      }, 0);
    }, 0);

  const handleCreate = async () => {
    if (creating || selected.size === 0) return;
    setCreating(true);
    try {
      const res = await api.createPaymentConfirmation(invoiceId, [...selected]);
      const data = res.data || {};
      showToast(`Potvrzení platby ${data.number} vystaveno.`, 'success');
      (data.warnings || []).forEach((w) => showToast(w, 'warning'));
      window.dispatchEvent(new CustomEvent('invoices:refresh'));
      onHide();
      if (data.id && openTab) {
        openTab(`invoice-${data.id}`, `Faktura ${data.number}`, `/invoices/${data.id}`, 'InvoiceDetail', { invoiceId: data.id });
      }
    } catch (e) {
      showToast(e.message || 'Potvrzení platby se nepodařilo vystavit.', 'danger');
    } finally {
      setCreating(false);
    }
  };

  const pendingAmount = info?.pending_amount || 0;
  const mismatch = info && Math.abs(selectedTotal - pendingAmount) > 0.01;

  return (
    <Modal show={show} onHide={() => !creating && onHide()} centered size="lg">
      <Modal.Header closeButton={!creating}>
        <Modal.Title><i className="bi bi-receipt-cutoff me-2" />Vytvořit potvrzení platby</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" className="me-2" />Načítání…</div>
        ) : info ? (
          <>
            <div className="small text-muted mb-3">
              Záloha <strong>{info.number}</strong> · {info.customer_name} · uhrazeno{' '}
              {formatPrice(info.paid_amount)} z {formatPrice(info.total_price)} {info.currency}
            </div>

            {info.pending_payments.length === 0 ? (
              <Alert variant="warning" className="py-2">
                Na záloze není žádná nepotvrzená platba — potvrzení se vystavuje k přijaté platbě.
              </Alert>
            ) : (
              <>
                <div className="small fw-bold mb-1">Platby k potvrzení</div>
                <Table size="sm" className="align-middle mb-3">
                  <tbody>
                    {info.pending_payments.map((p) => (
                      <tr key={p.id}>
                        <td className="text-nowrap">{formatDate(p.date)}</td>
                        <td className="small text-muted">{p.variable_symbol || ''}</td>
                        <td className="text-end fw-bold">{formatPrice(p.amount)} {info.currency}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <div className="small fw-bold mb-1">Položky, které platba pokrývá</div>
                <Table size="sm" hover className="align-middle mb-2">
                  <tbody>
                    {mainItems.map((item) => {
                      const discounts = discountsFor(item.number);
                      return (
                        <tr key={item.id} role="button" onClick={() => toggle(item.id)}>
                          <td style={{ width: 36 }} onClick={(e) => e.stopPropagation()}>
                            <Form.Check type="checkbox" checked={selected.has(item.id)} onChange={() => toggle(item.id)} />
                          </td>
                          <td>
                            <div>{item.name}</div>
                            {discounts.map((d) => (
                              <div key={d.id} className="small text-muted ms-3">{d.name} {formatPrice(d.price)}</div>
                            ))}
                          </td>
                          <td className="text-end text-nowrap">{formatPrice(item.price)} {info.currency}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                {mismatch && (
                  <Alert variant="warning" className="py-2 small">
                    <i className="bi bi-exclamation-triangle me-1" />
                    Vybrané položky dávají {formatPrice(selectedTotal)} {info.currency}, přijatá platba je{' '}
                    {formatPrice(pendingAmount)} {info.currency}. Doklad půjde vystavit, jen to zkontroluj.
                  </Alert>
                )}
              </>
            )}
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <div className="me-auto small text-muted">
          Vybráno {formatPrice(selectedTotal)} {info?.currency || 'CZK'} s DPH
        </div>
        <Button variant="outline-secondary" onClick={onHide} disabled={creating}>Zrušit</Button>
        <Button
          variant="primary"
          onClick={handleCreate}
          disabled={loading || creating || selected.size === 0 || !(info?.pending_payments || []).length}
        >
          {creating
            ? <><Spinner animation="border" size="sm" className="me-1" />Vystavuji…</>
            : <><i className="bi bi-check-lg me-1" />Vystavit potvrzení</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default InvoiceAdvanceModal;
